/**
 * Collection Category Sticky Bar & Mobile Swipe
 *
 * Desktop: Converts category cards to sticky horizontal menu on scroll
 * Mobile: Enables horizontal swipe with snap points and auto-centers active card
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    stickyThreshold: 100, // pixels scrolled before sticky bar appears
    mobileBreakpoint: 768,
    scrollDebounce: 10,
    arrowHideDelay: 2000
  };

  // DOM Elements
  let stickyBar = null;
  let categoryCardsWrapper = null;
  let categoryCards = null;
  let leftArrow = null;
  let rightArrow = null;
  let cardsContainer = null;
  let cardsTop = null;

  // State
  let isInitialized = false;
  let arrowTimeout = null;
  let lastScrollY = 0;
  let ticking = false;

  /**
   * Initialize the sticky bar functionality
   */
  function init() {
    if (isInitialized) return;

    // Get DOM elements
    stickyBar = document.querySelector('[data-category-sticky-bar]');
    categoryCardsWrapper = document.querySelector('[data-category-cards-wrapper]');
    categoryCards = document.querySelector('[data-category-cards]');
    leftArrow = document.querySelector('[data-scroll-arrow="left"]');
    rightArrow = document.querySelector('[data-scroll-arrow="right"]');
    cardsTop = document.querySelector('[data-category-cards-section]');

    if (!stickyBar && !categoryCardsWrapper) {
      return;
    }

    // Get the actual cards container (for scrolling)
    if (categoryCardsWrapper) {
      cardsContainer = categoryCardsWrapper.querySelector('.filter-category-cards');
    } else if (cardsTop) {
      cardsContainer = cardsTop.querySelector('.filter-category-cards');
    }

    // Bind events
    bindEvents();

    // Initial state check
    if (isMobile()) {
      initMobileSwipe();
    } else {
      initDesktopSticky();
    }

    isInitialized = true;
  }

  /**
   * Check if we're on mobile
   */
  function isMobile() {
    return window.innerWidth <= CONFIG.mobileBreakpoint;
  }

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Scroll handler for desktop sticky
    window.addEventListener('scroll', onScroll, { passive: true });

    // Resize handler
    window.addEventListener('resize', debounce(onResize, 150));

    // Arrow click handlers for mobile
    if (leftArrow) {
      leftArrow.addEventListener('click', function() {
        scrollCards('left');
      });
    }

    if (rightArrow) {
      rightArrow.addEventListener('click', function() {
        scrollCards('right');
      });
    }

    // Scroll handler for cards container (mobile)
    if (cardsContainer) {
      cardsContainer.addEventListener('scroll', debounce(onCardsScroll, 50), { passive: true });
    }
  }

  /**
   * Scroll event handler
   */
  function onScroll() {
    lastScrollY = window.scrollY;

    if (!ticking) {
      window.requestAnimationFrame(function() {
        updateStickyBar();
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * Update sticky bar visibility (Desktop only)
   */
  function updateStickyBar() {
    if (!stickyBar || isMobile()) return;

    // Get the position of the category cards section
    if (cardsTop) {
      const cardsRect = cardsTop.getBoundingClientRect();
      const cardsBottom = cardsRect.bottom;

      // Show sticky bar when cards section is scrolled out of view
      if (cardsBottom < 0) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    } else {
      // Fallback: use threshold
      if (lastScrollY > CONFIG.stickyThreshold) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    }
  }

  /**
   * Initialize desktop sticky behavior
   */
  function initDesktopSticky() {
    if (!stickyBar) return;

    // Initial check
    updateStickyBar();
  }

  /**
   * Initialize mobile swipe behavior
   */
  function initMobileSwipe() {
    if (!cardsContainer || !categoryCardsWrapper) return;

    // Auto-center active card on load
    const activeCard = cardsContainer.querySelector('.filter-category-card.is-active');
    if (activeCard) {
      setTimeout(function() {
        scrollToCard(activeCard);
      }, 100);
    }

    // Update arrow visibility
    updateScrollIndicators();

    // Show arrows briefly to indicate scrollability
    showArrowsBriefly();
  }

  /**
   * Scroll to center a specific card
   */
  function scrollToCard(card) {
    if (!card || !cardsContainer) return;

    const containerRect = cardsContainer.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    const scrollOffset = card.offsetLeft - (containerRect.width / 2) + (cardRect.width / 2);

    cardsContainer.scrollTo({
      left: scrollOffset,
      behavior: 'smooth'
    });
  }

  /**
   * Scroll cards left or right
   */
  function scrollCards(direction) {
    if (!cardsContainer) return;

    const cards = cardsContainer.querySelectorAll('.filter-category-card');
    if (!cards.length) return;

    const cardWidth = cards[0].offsetWidth + 12; // card width + gap
    const scrollAmount = cardWidth * 2; // scroll 2 cards at a time

    const currentScroll = cardsContainer.scrollLeft;
    const newScroll = direction === 'left'
      ? currentScroll - scrollAmount
      : currentScroll + scrollAmount;

    cardsContainer.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }

  /**
   * Handle cards container scroll (mobile)
   */
  function onCardsScroll() {
    updateScrollIndicators();
  }

  /**
   * Update scroll indicators (arrows and fade)
   */
  function updateScrollIndicators() {
    if (!cardsContainer || !categoryCardsWrapper) return;

    const scrollLeft = cardsContainer.scrollLeft;
    const scrollWidth = cardsContainer.scrollWidth;
    const clientWidth = cardsContainer.clientWidth;

    const canScrollLeft = scrollLeft > 10;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 10;

    // Update wrapper classes for fade effect
    categoryCardsWrapper.classList.toggle('can-scroll-left', canScrollLeft);
    categoryCardsWrapper.classList.toggle('can-scroll-right', canScrollRight);

    // Update arrow visibility
    if (leftArrow) {
      leftArrow.classList.toggle('is-visible', canScrollLeft);
    }
    if (rightArrow) {
      rightArrow.classList.toggle('is-visible', canScrollRight);
    }
  }

  /**
   * Show arrows briefly on load
   */
  function showArrowsBriefly() {
    if (!isMobile()) return;

    // Check if there's content to scroll
    if (cardsContainer && cardsContainer.scrollWidth > cardsContainer.clientWidth) {
      if (rightArrow) {
        rightArrow.classList.add('is-visible');
      }

      // Hide after delay
      arrowTimeout = setTimeout(function() {
        updateScrollIndicators();
      }, CONFIG.arrowHideDelay);
    }
  }

  /**
   * Handle resize
   */
  function onResize() {
    if (isMobile()) {
      // Hide sticky bar on mobile
      if (stickyBar) {
        stickyBar.classList.remove('is-visible');
      }
      // Re-init mobile swipe
      initMobileSwipe();
    } else {
      // Init desktop sticky
      initDesktopSticky();
    }
  }

  /**
   * Debounce utility
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.collection-category-cards-top')) {
      isInitialized = false;
      init();
    }
  });

})();
